import { Button, Grid, makeStyles, MenuItem, TextField } from '@material-ui/core';
import { Search } from '@material-ui/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import UniDialog from '../Common/UniDialog';
import _ from 'lodash';
import { DataGrid } from '@material-ui/data-grid';
import ErrorIcon from '@material-ui/icons/Error';
import { useDispatch, useSelector } from 'react-redux';
import { completeTask, forwardOrder } from '../../actions/orderExchangeActions';
import { getBranchById } from '../../utilities/common';
import { showAlertSnackbar } from '../../reducers/uiSlice';
import FullscreenLoading from '../Common/FullscreenLoading';

const useStyles = makeStyles((theme) => ({
    statusSection: {
        display: 'flex',
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(2),
        paddingLeft: theme.spacing(1),
        alignItems: 'center'
    },
    summarySection: {
        color: theme.palette.text.primary,
        display: 'flex',
        alignItems: "center"
    },
    groupSection: {
        marginRight: theme.spacing(4),
        display: 'flex',
        alignItems: 'center'
    },
}))

export default function CreateDetails(props) {
    const {t} = useTranslation('orderForwardTool');
    const classes = useStyles();
    const dispatch = useDispatch();

    const statuses = {
        '0': {
            label: 'pending'
        },
        '1': {
            label: 'orderPlaced'
        },
        '2': {
            label: 'orderPrinted'
        },
        '3': {
            label: 'failedToForward'
        }
    }

    const thirdPartyList = [
        {
            id: -1,
            code: 'auto',
            text: t('auto')
        },
        {
            id: 0,
            code: 'CP',
            text: 'Canada Post'
        },
        {
            id: 1,
            code: 'FD',
            text: 'FedEx'
        },
        {
            id: 2,
            code: 'GV',
            text: 'GV'
        }
    ]

    const labelSizeList = [
        {
            id: 0,
            text: '4" x 6"'
        },
        {
            id: 1,
            text: '10cm x 15cm'
        },
        {
            id: 2,
            text: '10cm x 10cm'
        },
        {
            id: 3,
            text: 'A4'
        }
    ]

    const columns = [
        {field: 'id', hide: true},
        {
            field: 'tno', 
            headerName: t('tracking_no'), 
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            sortable: false
        },
        {
            field: 'warehouse', 
            headerName: t('warehouse'), 
            flex: 1,
            headerAlign: 'center',
            valueFormatter: (params) => getBranchById(params.value).name,
            align: 'center',
            sortable: false
        },
        {
            field: 'third_party_tno', 
            headerName: t('third_party_tracking_no'), 
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <div>
                    {params.value || '-'}
                </div>
            ),
            sortable: false
        },
        {
            field: 'transship_status', 
            headerName: t('status'), 
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            renderCell: (params) => (
                <div style={{display: 'flex', alignItems: 'center'}}>
                    <span>{props.statuses[params.value + '']?.text || params.value}</span>
                    {
                        params.row.transship_status === '3' &&
                        <span><ErrorIcon style={{color: 'red', marginLeft: 5}}/></span>
                    }
                </div>
            ),
            sortable: false
        },
        {
            field: 'actions', 
            headerName: t('actions'), 
            flex: 1,
            headerAlign: 'center',
            renderCell: (params) => (
                <Button
                    disabled={params.row.transship_status !== '2'}
                    onClick={() => {window.open(params.row.label_url, '_blank');}}
                    color='primary'
                >
                    {t('print')}
                </Button>
            ),
            align: 'center',
            sortable: false
        }
    ]


    const [thirdParty, setThirdParty] = useState('auto');
    const [labelSize, setLabelSize] = useState(0);
    const [trackingNo, setTrackingNo] = useState();
    const [orderList, setOrderList] = useState(props.orderList);
    const [summary, setSummary] = useState({...props.summary, orderPrinted: 0});
    const [loading, setLoading] = useState(false);

    const handleKeyUp = (e) => {
        if(e.keyCode === 13) {
            handleScan();
        }
    }

    const createRows = () => {
        return orderList.map((row) => {
            const {order_id, ...rest} = row
            return {id: order_id, ...rest}
        })
    }

    const handleScan = () => {
        const orderScanned = _.find(props.orderList, {tno: trackingNo});
        if(orderScanned) {
            const prevStatus = orderScanned.transship_status;
            setLoading(true);
            dispatch(forwardOrder({
                task_id: props.task.taskId,
                tno: orderScanned.tno,
                carrier_code: thirdParty === 'auto' ? '' : thirdParty
            }))
            .then((res) => {
                let printSuccess = false;
                const orderListClone = _.filter(orderList, (order) => {
                    if(order.order_id === orderScanned.order_id) {
                        if(res.transship_status + '' === '2' || res.exchange_status + '' === '2') {
                            order.third_party_tno = res.third_party_tno;
                            order.label_url = res.lable_info[0].labelPath;
                            dispatch(showAlertSnackbar({message: t('enable_print'), type: 'success'}));
                            printSuccess = true;
                        }
                        order.transship_status = res.exchange_status + '';
                        handleSummary(order, prevStatus);
                    }
                    return order;
                })
                if(!printSuccess) {
                    dispatch(showAlertSnackbar({message: t('enable_print_fail'), title: t('enable_print_fail_title'), type: 'warning'}));
                }
                setOrderList(orderListClone);
            })
            .catch(() => {
                dispatch(showAlertSnackbar({message: t('failed_to_forward_alert'), title: t('failed_to_forward_title'), type: 'error'}))
            })
            .finally(() => {
                setLoading(false);
            })
        } else {
            dispatch(showAlertSnackbar({message: t('wrong_parcel'), title: t('wrong_parcel_title'), type: 'error'}))
        }
    }

    const handleSummary = (order, prevStatus) => {
        if(order.transship_status + '' === prevStatus + '') {
            return;
        }
        if(order.transship_status + '' === '0') {
            setSummary((prev) => ({
                ...prev,
                [statuses[prevStatus + ''].label]: summary[statuses[prevStatus + ''].label] - 1,
                ['pending']: summary.pending + 1
            }))
        } else if(order.transship_status + '' === '1') {
            setSummary((prev) => ({
                ...prev,
                [statuses[prevStatus + ''].label]: summary[statuses[prevStatus + ''].label] - 1,
                ['orderPlaced']: summary.orderPlaced + 1
            }))
        } else if(order.transship_status + '' === '2') {
            setSummary((prev) => ({
                ...prev,
                [statuses[prevStatus + ''].label]: summary[statuses[prevStatus + ''].label] - 1,
                ['orderPrinted']: summary.orderPrinted + 1
            }))
        } else if(order.transship_status + '' === '3') {
            setSummary((prev) => ({
                ...prev,
                [statuses[prevStatus + ''].label]: summary[statuses[prevStatus + ''].label] - 1,
                ['failedToForward']: summary.failedToForward + 1
            }))
        }
    } 

    const handleComplete = () => {
        dispatch(completeTask({task_id: props.task.taskId}))
        .then((res) => {
            dispatch(showAlertSnackbar({message: t('complete_success'), type: 'success'}));
            props.handleClose();
        })
        .catch(() => {
            dispatch(showAlertSnackbar({message: t('failed_to_upload'), title: t('failed_to_upload_title'), type: 'error'}))
        })
    }


    useEffect(() => {
        setOrderList(props.orderList);
    }, [props.orderList])

    useEffect(() => {
        setSummary({...props.summary, orderPrinted: 0});
    }, [props.summary])

    useEffect(() => {
        createRows();
    }, [orderList])

    return (
        <>
            <UniDialog
                title={props.task.taskName}
                maxWidth='lg'
                open={props.open}
                onClose={props.handleClose}
                buttons={[
                    {
                        color: 'primary',
                        text: t('complete'),
                        action: () => {
                            handleComplete();
                        },
                        id: 'orderforwardtool-createdetail-complete-button'
                    }
                ]}
                fullWidth
            >
                <Grid container spacing={2} style={{display:'flex',alignItems:'center'}}>
                    <Grid item xs={2}>
                        <TextField
                            select
                            label={t('third_party')}
                            value={thirdParty}
                            onChange={(e)=>setThirdParty(e.target.value)}
                            variant='outlined'
                            fullWidth
                            id='orderforwardtool-createdetail-thirdparty-textfield'
                        >
                        {
                            thirdPartyList.map((thirdParty) => (
                                <MenuItem key={thirdParty.id} value={thirdParty.code}>{thirdParty.text}</MenuItem>
                            ))
                        }
                        </TextField>
                    </Grid>
                    {/* <Grid item xs={2}>
                        <TextField
                            select
                            label={t('label_size')}
                            value={labelSize}
                            onChange={(e)=>setLabelSize(e.target.value)}
                            variant='outlined'
                            fullWidth
                            id='orderforwardtool-createdetail-labelsize-textfield'
                        >
                        {
                            labelSizeList.map((label) => (
                                <MenuItem key={label.id} value={label.id}>{label.text}</MenuItem>
                            ))
                        }
                        </TextField>
                    </Grid> */}
                    <Grid item xs={6} style={{display:'flex',alignItems:'center'}}>
                        <span style={{marginRight:10}}>{t('scan_parcel_or_manual_input')}</span>
                        <TextField
                            label={t('tracking_no')}
                            value={trackingNo}
                            onChange={(e)=>setTrackingNo(e.target.value)}
                            variant='outlined'
                            fullWidth
                            onKeyUp={(e)=>handleKeyUp(e)}
                            id='orderforwardtool-createdetail-search-textfield'
                        />
                    </Grid>
                    <Grid item xs={2}>
                        <Search
                            color='primary'
                            style={{cursor:'pointer'}}
                            id='orderforwardtool-createdetail-search-button'
                            onClick={handleScan}
                        />
                    </Grid>
                </Grid>
                <div className={classes.statusSection}>
                    <div className={classes.summarySection}>
                        <div className={classes.groupSection}>
                            <span>{t('pending')}: </span>
                            <span>{summary.pending}</span>
                        </div>
                        <div className={classes.groupSection}>
                            <span>{t('order_placed')}: </span>
                            <span>{summary.orderPlaced}</span>
                        </div>
                        <div className={classes.groupSection}>
                            <span>{t('label_printed')}: </span>
                            <span>{summary.orderPrinted}</span>
                        </div>
                        <div style={{display:'flex',alignItems:'center'}}>
                            <ErrorIcon style={{color: 'red', marginRight: 5}}/>
                            <span style={{marginRight:5}}>{t('failed_to_forward')}: </span>
                            <span style={{color: 'red'}}>{summary.failedToForward}</span>
                        </div>
                    </div>
                </div>
                <DataGrid
                    style={{height:'60vh',marginTop:20}}
                    alignItems="center"
                    rows={createRows()}
                    columns={columns}
                    pageSize={20}
                    disableSelectionOnClick
                />
            </UniDialog>
            <FullscreenLoading open={loading}/>
        </>
    );
}